import React, { useEffect, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    getAvailableStudentsForTutor,
    getOrCreateChatBox,
    getUnreadMessageCount,
    listenToMessages,
    listenToUserChatBoxes,
    markMessagesAsRead,
    sendMessage,
    uploadMedia,
    MEDIA_TYPES
} from '../../../../store/services/chatService';

import AudioRecorder from '../../../components/Chat/AudioRecorder';
import MediaAttachmentModal from '../../../components/Chat/MediaAttachmentModal.jsx';
import MediaMessage from '../../../components/Chat/MediaMessage';

//images
import BasicModal from '../../../components/Dashboard/BasicModal';
import { SVGICON } from '../../../components/Dashboard/Content';
import course1 from './../../../../images/coures/1.jpg';
import course2 from './../../../../images/coures/2.jpg';
import course3 from './../../../../images/coures/3.jpg';
import course4 from './../../../../images/coures/4.jpg';
import course5 from './../../../../images/coures/5.jpg';
import course6 from './../../../../images/coures/6.jpg';
import course7 from './../../../../images/coures/7.jpg';
import {
    formatMessageDate,
    formatMessageTime,
    groupMessagesByDate,
    shouldGroupMessages
} from '../../../../utils/dateUtils';

const chatList = [
    { image1: course2, title1: 'Product Team (32)', time1: '10.12', image2: course5, title2: 'Design Team (32)', time2: '11.20' },
    { image1: course3, title1: 'Tony Soap', time1: '10.20', image2: course6, title2: 'Tony Soap', time2: '11.30' },
    { image1: course4, title1: 'Karen Hope', time1: '10.50', image2: course7, title2: 'Karen Hope', time2: '11.45', change: 'active' }
];

export const TutorChat = () => {
    const childRef = useRef();
    const chatContainerRef = useRef(null);
    const { currentUser } = useSelector(state => state.auth);
    const tutorId = currentUser?.tutorId || currentUser?.id;

    const [chatBoxes, setChatBoxes] = useState([]);
    const [availableStudents, setAvailableStudents] = useState([]);
    const [selectedChatBox, setSelectedChatBox] = useState(null);
    const [messages, setMessages] = useState([]);
    const [groupedMessages, setGroupedMessages] = useState({});
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [unreadCounts, setUnreadCounts] = useState({});
    const [studentsLoading, setStudentsLoading] = useState(false);
    // Inside the component function:
    const [showMediaModal, setShowMediaModal] = useState(false);
    const [uploadingMedia, setUploadingMedia] = useState(false);
    const [showAudioRecorder, setShowAudioRecorder] = useState(false);
    const [recordingAudio, setRecordingAudio] = useState(false);

    // Listen to user's chat boxes with real student data
    useEffect(() => {
        if (tutorId) {
            const unsubscribe = listenToUserChatBoxes(tutorId, 'tutor', (userChatBoxes) => {
                setChatBoxes(userChatBoxes);
            });

            return () => unsubscribe();
        }
    }, [tutorId]);

    // Group messages by date when messages change
    useEffect(() => {
        if (messages.length > 0) {
            const grouped = groupMessagesByDate(messages);
            setGroupedMessages(grouped);
        } else {
            setGroupedMessages({});
        }
    }, [messages]);

    // Auto-scroll to bottom
    useEffect(() => {
        if (chatContainerRef.current) {
            const container = chatContainerRef.current;
            setTimeout(() => {
                container.scrollTop = container.scrollHeight;
            }, 100);
        }
    }, [messages, selectedChatBox, groupedMessages]);

    // Load available students for new chats
    useEffect(() => {
        const loadAvailableStudents = async () => {
            if (!tutorId) return;

            setStudentsLoading(true);
            try {
                const students = await getAvailableStudentsForTutor(tutorId);
                setAvailableStudents(students);
            } catch (error) {
                console.error('Error loading available students:', error);
                setAvailableStudents([]);
            } finally {
                setStudentsLoading(false);
            }
        };

        loadAvailableStudents();
    }, [tutorId]);

    // Listen to unread message counts
    useEffect(() => {
        if (tutorId) {
            const unsubscribe = getUnreadMessageCount(tutorId, (count) => {
                setUnreadCounts(prev => ({ ...prev, total: count }));
            });

            return () => unsubscribe();
        }
    }, [tutorId]);

    // Listen to messages when a chat box is selected
    useEffect(() => {
        if (selectedChatBox?.id && tutorId) {
            const unsubscribe = listenToMessages(selectedChatBox.id, (chatMessages) => {
                setMessages(chatMessages);
                markMessagesAsRead(selectedChatBox.id, tutorId);

                setUnreadCounts(prev => ({
                    ...prev,
                    [selectedChatBox.id]: 0
                }));
            });

            return () => unsubscribe();
        }
    }, [selectedChatBox, tutorId]);

    // Start a new chat
    const startNewChat = async (studentId, studentName) => {
        if (!tutorId || !studentId) return;

        try {
            setLoading(true);
            const chatBox = await getOrCreateChatBox(
                tutorId,
                studentId,
                'tutor',
                'student'
            );

            // Enrich chat box with student info for immediate display
            const enrichedChatBox = {
                ...chatBox,
                studentId: studentId,
                otherParticipantName: studentName,
                otherParticipantType: 'student',
                otherParticipantImage: null
            };

            setSelectedChatBox(enrichedChatBox);
            childRef.current?.closeModal();
        } catch (error) {
            console.error('Error starting new chat:', error);
        } finally {
            setLoading(false);
        }
    };

    // Send a message
    const handleSendMessage = async () => {
        if (!newMessage.trim() || !selectedChatBox || !tutorId) return;

        try {
            setLoading(true);

            // Get the receiver ID (student ID)
            const receiverId = selectedChatBox.ids.find(id => id !== parseInt(tutorId));

            await sendMessage(
                selectedChatBox.id,
                tutorId,
                receiverId,
                newMessage,
                'tutor',
                'NONE'
            );

            setNewMessage('');
        } catch (error) {
            console.error('Error sending message:', error);
        } finally {
            setLoading(false);
        }
    };

    // Add this function to handle audio recording completion
    const handleAudioRecordingComplete = async (audioFile) => {
        if (!audioFile || !selectedChatBox || !currentUser?.id) return;

        try {
            setRecordingAudio(true);

            // Upload audio file
            const uploadResult = await uploadMedia(
                audioFile,
                selectedChatBox.id,
                currentUser.id
            );

            // Get receiver ID
            const receiverId = selectedChatBox.ids.find(id => id !== parseInt(currentUser.id));

            // Send audio message
            await sendMessage(
                selectedChatBox.id,
                tutorId,
                receiverId,
                newMessage, // You can keep any text message along with audio
                'tutor',
                MEDIA_TYPES.AUDIO,
                uploadResult.url,
                uploadResult.duration
            );

            // Clear text message if any
            setNewMessage('');

        } catch (error) {
            console.error('Error sending audio message:', error);
            alert('Failed to send audio message. Please try again.');
        } finally {
            setRecordingAudio(false);
        }
    };

    // Handle enter key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };
    // In StudentChat.jsx or TutorChat.jsx
    const handleMediaSelect = async (files, mediaTypes) => {
        if (!files || files.length === 0) return;

        try {
            setUploadingMedia(true);

            // Handle multiple files if needed
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const mediaType = mediaTypes[i];

                if (mediaType === MEDIA_TYPES.IMAGE ||
                    mediaType === MEDIA_TYPES.VIDEO ||
                    mediaType === MEDIA_TYPES.AUDIO) {

                    // For file uploads
                    const uploadResult = await uploadMedia(file, selectedChatBox.id, currentUser.id);

                    // Send message with media
                    await sendMessage(
                        selectedChatBox.id,
                        tutorId,
                        selectedChatBox.ids.find(id => id !== parseInt(currentUser.tutorId)),
                        newMessage,
                        'tutor',
                        uploadResult.type,
                        uploadResult.url,
                        uploadResult.duration
                    );
                } else {
                    // For non-file media types
                    await sendMessage(
                        selectedChatBox.id,
                        tutorId,
                        selectedChatBox.ids.find(id => id !== parseInt(currentUser.tutorId)),
                        newMessage,
                        'tutor',
                        mediaType,
                        null, // No media_path
                        null, // No duration
                        file   // Pass data as media_data
                    );
                }
            }

            setNewMessage('');
        } catch (error) {
            console.error('Error sending media:', error);
            alert('Failed to send media. Please try again.');
        } finally {
            setUploadingMedia(false);
        }
    };

    // Get message status text
    const getMessageStatus = (status) => {
        switch (status) {
            case 0: return 'Sent';
            case 1: return 'Delivered';
            case 2: return 'Read';
            default: return 'Sent';
        }
    };

    // Filter chat boxes based on search
    const filteredChatBoxes = chatBoxes.filter(chatBox =>
        chatBox.otherParticipantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        chatBox.last_message.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Get unread count for a specific chat box
    const getUnreadCountForChat = (chatBoxId) => {
        console.log("chatBoxId = ", chatBoxId);
        console.log("unreadCounts2 = ", unreadCounts);
        return unreadCounts.total[chatBoxId] || 0;
    };

    // Format time for chat list
    const formatChatTime = (timestamp) => {
        if (!timestamp) return '';

        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    return (
        <>
            <div className="row gx-0 bg-white">
                <div className="col-xl-3 col-xxl-4 col-md-5 chat-left-area">
                    <div className="card dlab-scroll chat-sidebar rounded-0 mb-0">
                        <div className="card-body">
                            <div className="message-box d-flex align-items-center justify-content-between border-0">
                                <div className="input-group search-area">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search chats..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    <span className="input-group-text">
                                        <Link to={"#"}>
                                            {SVGICON.search}
                                        </Link>
                                    </span>
                                </div>
                            </div>

                            {/* Chat Boxes List */}
                            <div className="chat-tabs">
                                <h4>Your Chats</h4>
                                <div className="course-details-tab style-2">
                                    <ul>
                                        {filteredChatBoxes.map((chatBox) => {
                                            const unreadCount = getUnreadCountForChat(chatBox.id);
                                            console.log("unreadCount = ", unreadCount);
                                            return (
                                                <li
                                                    key={chatBox.id}
                                                    className={`chat-bx chats ${selectedChatBox?.id === chatBox.id ? 'active' : ''}`}
                                                    onClick={() => setSelectedChatBox(chatBox)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <div className="chat-img">
                                                        <img
                                                            src={chatBox.otherParticipantImage || course5}
                                                            alt={chatBox.otherParticipantName}
                                                            onError={(e) => { e.target.src = course5; }}
                                                        />
                                                    </div>
                                                    <div className="mid-info">
                                                        <h4 className="name">{chatBox.otherParticipantName}</h4>
                                                        <span className="text-muted">
                                                            {chatBox.last_message || 'No messages yet'}
                                                        </span>
                                                    </div>
                                                    <div className="right-info text-end">
                                                        <p className="mb-1">{formatChatTime(chatBox.lastMessageTimeFormatted)}</p>
                                                        {/* Unread count badge in the right corner */}
                                                        {unreadCount > 0 && (
                                                            <span className="badge bg-primary rounded-pill">
                                                                {unreadCount > 99 ? '99+' : unreadCount}
                                                            </span>
                                                        )}
                                                    </div>
                                                </li>
                                            )
                                        })}

                                        {filteredChatBoxes.length === 0 && !loading && (
                                            <li className="text-center text-muted py-3">
                                                {tutorId ? 'No chats yet' : 'Please login to view chats'}
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            </div>

                            {!tutorId && (
                                <div className="alert alert-warning mt-3">
                                    <small>Please login to start chatting with students</small>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Chat Area */}
                <div className="col-xl-9 col-xxl-8 col-md-7 chat-mid-area bg-white">
                    {selectedChatBox ? (
                        <>
                            <div className="message-box style-1 d-flex align-items-center">
                                <img
                                    src={selectedChatBox.otherParticipantImage || course1}
                                    alt={selectedChatBox.otherParticipantName}
                                    className="rounded-circle"
                                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                />
                                <div className="ms-2 ms-sm-3 flex-1">
                                    <h4 className="mb-1">{selectedChatBox.otherParticipantName}</h4>
                                    <span className="text-muted">
                                        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="8" cy="8" r="8" fill="#4CBC9A"></circle>
                                        </svg>
                                        {" "}Online
                                    </span>
                                </div>
                                <div className="chat-hamburger ms-auto">
                                    <div className="chat-toggle">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="32px" height="32px" viewBox="0 0 24 24" version="1.1" className="svg-main-icon">
                                            <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                                                <rect x="0" y="0" width="24" height="24"></rect>
                                                <rect fill="#008ddd" x="4" y="4" width="7" height="7" rx="1.5"></rect>
                                                <path d="M5.5,13 L9.5,13 C10.3284271,13 11,13.6715729 11,14.5 L11,18.5 C11,19.3284271 10.3284271,20 9.5,20 L5.5,20 C4.67157288,20 4,19.3284271 4,18.5 L4,14.5 C4,13.6715729 4.67157288,13 5.5,13 Z M14.5,4 L18.5,4 C19.3284271,4 20,4.67157288 20,5.5 L20,9.5 C20,10.3284271 19.3284271,11 18.5,11 L14.5,11 C13.6715729,11 13,10.3284271 13,9.5 L13,5.5 C13,4.67157288 13.6715729,4 14.5,4 Z M14.5,13 L18.5,13 C19.3284271,13 20,13.6715729 20,14.5 L20,18.5 C20,19.3284271 19.3284271,20 18.5,20 L14.5,20 C13.6715729,20 13,19.3284271 13,18.5 L13,14.5 C13,13.6715729 13.6715729,13 14.5,13 Z" fill="#666666" opacity="0.8"></path>
                                            </g>
                                        </svg>
                                    </div>
                                    {/* <div className="videos-player">
                                        <Link to={"#"} className="videos-btn">
                                            <svg width="25" height="25" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M19.9997 8H3.99967C2.52691 8 1.33301 9.19391 1.33301 10.6667V21.3333C1.33301 22.8061 2.52691 24 3.99967 24H19.9997C21.4724 24 22.6663 22.8061 22.6663 21.3333V10.6667C22.6663 9.19391 21.4724 8 19.9997 8Z" stroke="#A098AE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                                                <path d="M30.666 9.33325V22.6666L22.666 15.9999L30.666 9.33325Z" stroke="#A098AE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                                            </svg>
                                        </Link>
                                    </div> */}
                                    {/* <Dropdown className="custom-dropdown">
                                        <Dropdown.Toggle as="div" className="i-false btn sharp tp-btn ">
                                            {SVGICON.dots}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu className="dropdown-menu-end" align="end">
                                            <Dropdown.Item>Option 1</Dropdown.Item>
                                            <Dropdown.Item>Option 2</Dropdown.Item>
                                            <Dropdown.Item>Option 3</Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown> */}
                                </div>
                            </div>

                            <div className="chart-content">
                                <div
                                    className="chat-box-area dlab-scroll"
                                    id="chartBox"
                                    ref={chatContainerRef}
                                    style={{
                                        maxHeight: '500px',
                                        overflowY: 'auto',
                                        padding: '15px'
                                    }}
                                >
                                    {Object.keys(groupedMessages).length > 0 ? (
                                        Object.entries(groupedMessages).map(([dateKey, group]) => (
                                            <div key={dateKey}>
                                                {/* Date Separator */}
                                                <div className="date-separator text-center my-4">
                                                    <span className="badge bg-light text-dark px-3 py-2 rounded-pill shadow-sm">
                                                        <i className="fas fa-calendar-alt me-2"></i>
                                                        {group.displayDate}
                                                    </span>
                                                </div>

                                                {/* Messages for this date */}
                                                {group.messages.map((message, index) => {
                                                    const isCurrentUser = message.sender_id === parseInt(tutorId);
                                                    const prevMessage = index > 0 ? group.messages[index - 1] : null;
                                                    const showAvatar = !shouldGroupMessages(prevMessage, message);
                                                    const showTime = !shouldGroupMessages(message, group.messages[index + 1]) ||
                                                        index === group.messages.length - 1;

                                                    return (
                                                        <div
                                                            key={message.id}
                                                            className={`media ${!showAvatar ? 'mt-1' : 'mt-3'} justify-content-${isCurrentUser ? 'end' : 'start'} align-items-start`}
                                                        >
                                                            {/* Student avatar (left side) */}
                                                            {/* {!isCurrentUser && showAvatar && (
                                                                <div className="me-2 flex-shrink-0">
                                                                    <img
                                                                        src={selectedChatBox.otherParticipantImage || course5}
                                                                        alt={selectedChatBox.otherParticipantName}
                                                                        className="rounded-circle border"
                                                                        style={{ 
                                                                            width: '36px', 
                                                                            height: '36px', 
                                                                            objectFit: 'cover' 
                                                                        }}
                                                                        onError={(e) => { e.target.src = course5; }}
                                                                    />
                                                                </div>
                                                            )} */}

                                                            {/* Message Bubble */}
                                                            <div
                                                                className={`${isCurrentUser ? 'message-sent' : 'message-received'} w-auto ${isCurrentUser ? 'me-2' : ''} ${!showAvatar ? 'no-avatar' : ''}`}
                                                                style={{
                                                                    // marginLeft: !isCurrentUser && !showAvatar ? '44px' : '0',
                                                                    // backgroundColor: isCurrentUser ? '#0084ff' : '#f0f0f0',
                                                                    // color: isCurrentUser ? 'white' : 'black',
                                                                    // padding: '8px 16px',
                                                                    // borderRadius: '18px',
                                                                    // maxWidth: '70%',
                                                                    // position: 'relative',
                                                                    // borderBottomLeftRadius: !isCurrentUser && !showAvatar ? '4px' : '18px',
                                                                    // borderBottomRightRadius: isCurrentUser && !showAvatar ? '4px' : '18px'
                                                                }}
                                                            >
                                                                {message.media_type && message.media_type !== MEDIA_TYPES.NONE ? (
                                                                    <MediaMessage
                                                                        message={message}
                                                                        isCurrentUser={message.sender_id == parseInt(currentUser?.tutorId)}
                                                                    />
                                                                ) : (
                                                                    <p className="mb-1" style={{ wordBreak: 'break-word' }}>
                                                                        {message.message}
                                                                    </p>
                                                                )}

                                                                {/* Message time and status */}
                                                                {showTime && (
                                                                    <div className="d-flex align-items-center justify-content-end mt-1">
                                                                        <span className="fs-12" style={{
                                                                            //color: isCurrentUser ? 'rgba(255,255,255,0.7)' : '#999',
                                                                            fontSize: '11px'
                                                                        }}>
                                                                            {formatMessageTime(message.created_at)}
                                                                        </span>
                                                                        {isCurrentUser && (
                                                                            <span className="fs-10 ms-2" style={{
                                                                                //color: 'rgba(255,255,255,0.7)',
                                                                                fontSize: '11px'
                                                                            }}>
                                                                                {getMessageStatus(message.message_status)}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                )}

                                                            </div>

                                                            {/* Tutor avatar (right side) */}
                                                            {/* {isCurrentUser && showAvatar && (
                                                                <div className="ms-2 flex-shrink-0">
                                                                    <img
                                                                        src={currentUser?.imageUrl ? `${process.env.REACT_APP_IMAGE_PATH}${currentUser.imageUrl}` : course5}
                                                                        alt="You"
                                                                        className="rounded-circle border"
                                                                        style={{ 
                                                                            width: '36px', 
                                                                            height: '36px', 
                                                                            objectFit: 'cover' 
                                                                        }}
                                                                        onError={(e) => { e.target.src = course5; }}
                                                                    />
                                                                </div>
                                                            )} */}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ))
                                    ) : messages.length === 0 ? (
                                        <div className="text-center text-muted py-5">
                                            <i className="fas fa-comments fa-3x mb-3" style={{ color: '#e0e0e0' }}></i>
                                            <h5 style={{ color: '#666' }}>No messages yet</h5>
                                            <p style={{ color: '#999' }}>Start the conversation!</p>
                                        </div>
                                    ) : null}
                                </div>

                                {/* Message Input */}
                                <div className="type-massage mt-3">
                                    <div className="input-group">
                                        <div className="input-group-prepend d-flex align-items-center">
                                            <button
                                                type="button"
                                                className="btn btn-light rounded-circle mr-2"
                                                onClick={() => setShowMediaModal(true)}
                                                disabled={uploadingMedia || recordingAudio}
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                {uploadingMedia ? (
                                                    <div className="spinner-border spinner-border-sm" role="status">
                                                        <span className="visually-hidden">Uploading...</span>
                                                    </div>
                                                ) : (
                                                    <i className="bi bi-paperclip" style={{ fontSize: '1.25rem' }}></i>
                                                )}
                                            </button>

                                            <button
                                                type="button"
                                                className="btn btn-light rounded-circle mr-2"
                                                onClick={() => setShowAudioRecorder(true)}
                                                disabled={uploadingMedia || recordingAudio}
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <i className="bi bi-mic-fill" style={{ fontSize: '1.25rem' }}></i>
                                            </button>
                                        </div>

                                        <textarea
                                            className="form-control"
                                            placeholder="Type your message..."
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            disabled={loading || uploadingMedia || recordingAudio}
                                            rows="1"
                                            style={{ borderRadius: '20px', padding: '10px 15px' }}
                                        />

                                        <div className="input-group-append">
                                            <button
                                                type="button"
                                                className="btn btn-primary rounded-circle ml-2"
                                                onClick={handleSendMessage}
                                                disabled={(!newMessage.trim() && !recordingAudio) || loading || uploadingMedia}
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                {loading ? (
                                                    <div className="spinner-border spinner-border-sm" role="status">
                                                        <span className="visually-hidden">Sending...</span>
                                                    </div>
                                                ) : (
                                                    <svg width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M17.466 0.0554752L0.96597 6.80548C0.816794 6.8708 0.692728 6.98259 0.612274 7.12419C0.53182 7.26578 0.499288 7.42958 0.519529 7.59117C0.53977 7.75276 0.6117 7.90348 0.724592 8.02085C0.837484 8.13822 0.98529 8.21596 1.14597 8.24248L9.14097 9.35923L10.2577 17.3542C10.2807 17.5174 10.3567 17.6685 10.4741 17.7841C10.5915 17.8997 10.7437 17.9735 10.9072 17.994C10.9381 17.9978 10.9691 17.9998 11.0002 18C11.149 17.9999 11.2944 17.9556 11.418 17.8727C11.5415 17.7897 11.6376 17.6719 11.694 17.5342L18.444 1.03423C18.4967 0.897557 18.5087 0.748508 18.4784 0.60517C18.4481 0.461832 18.3769 0.330368 18.2733 0.226735C18.1698 0.123102 18.0384 0.0517551 17.8951 0.0213547C17.7517 -0.0090457 17.6027 0.00280736 17.466 0.0554752ZM11.3587 14.3925L10.5502 8.59048C10.5275 8.42922 10.453 8.27973 10.3379 8.16459C10.2227 8.04944 10.0732 7.9749 9.91197 7.95223L4.10772 7.14148L16.3792 2.12098L11.3587 14.3925Z" fill="#FCFCFC" />
                                                    </svg>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Media Attachment Modal */}
                            <MediaAttachmentModal
                                show={showMediaModal}
                                onClose={() => setShowMediaModal(false)}
                                onSelectMedia={handleMediaSelect}
                                chatBoxId={selectedChatBox?.id}
                            />

                            {/* Audio Recorder Modal (overlay) */}
                            {showAudioRecorder && (
                                <div className="audio-recorder-overlay" style={{
                                    position: 'fixed',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(0,0,0,0.5)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 1050
                                }}>
                                    <AudioRecorder
                                        onRecordingComplete={handleAudioRecordingComplete}
                                        onClose={() => setShowAudioRecorder(false)}
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="d-flex justify-content-center align-items-center h-100">
                            <div className="text-center">
                                <i className="fas fa-comments fa-4x text-muted mb-4" style={{ color: '#e0e0e0' }}></i>
                                <h4 style={{ color: '#666' }}>Select a chat to start messaging</h4>
                                <p className="text-muted" style={{ color: '#999' }}>Choose a student from the list to begin your conversation</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal for starting new chat */}
            <BasicModal
                ref={childRef}
                title="Start New Chat with Student"
                content={
                    <div>
                        <h5 className="mb-3">Select a student to start chatting:</h5>
                        {studentsLoading ? (
                            <div className="text-center py-3">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading students...</span>
                                </div>
                                <p className="mt-2">Loading students...</p>
                            </div>
                        ) : availableStudents.length > 0 ? (
                            <div className="mt-3">
                                {availableStudents.map(student => (
                                    <div
                                        key={student.id}
                                        className="d-flex align-items-center p-3 border rounded mb-2 hover-shadow"
                                        style={{
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onClick={() => startNewChat(student.id, student.name)}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                    >
                                        <img
                                            src={student.imagePath ? `${process.env.REACT_APP_IMAGE_PATH}${student.imagePath}` : course1}
                                            alt={student.name}
                                            className="rounded-circle me-3 border"
                                            width="48"
                                            height="48"
                                            style={{ objectFit: 'cover' }}
                                            onError={(e) => { e.target.src = course1; }}
                                        />
                                        <div>
                                            <div className="fw-bold">{student.name}</div>
                                            <small className="text-muted">Student</small>
                                            {student.language && (
                                                <div className="mt-1">
                                                    <span className="badge bg-info bg-opacity-10 text-info">
                                                        {student.language}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center text-muted py-4">
                                <i className="fas fa-users fa-3x mb-3" style={{ color: '#e0e0e0' }}></i>
                                <p className="mb-2">No available students found.</p>
                                <small>All students you can message are already in your chat list.</small>
                            </div>
                        )}
                    </div>
                }
            />
        </>
    );
};